import React, { useMemo } from 'react';

import LoadingSpinner from '../../components/loading/nestedSpinner';
import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// hooks
import useActivityLogsQuery from 'hooks/activity-logs/useActivityLogsQuery';
import useAuditTrailFilterState, {
    AuditTrailFilterMode,
} from 'hooks/activity-logs/useAuditTrailFilterState';
import useAuditUserResolver from 'hooks/activity-logs/useAuditUserResolver';

// constants
import { ActivityLogModelType } from 'models/activty-log.model';

import AuditTrailFilters from '../activity-logs/AuditTrailFilters';
// components
import AuditTrailViewWrapper from '../activity-logs/AuditTrailViewWrapper';

type AuditTrailWrapperProps = {
    targetModel: ActivityLogModelType;
    targetId: string;
    /** 'url' keeps filters in the query string; 'local' keeps them in state (drawers). */
    filterMode?: AuditTrailFilterMode;
    hideHeading?: boolean;
    heading?: string;
};

const AuditTrailWrapper: React.FC<
    React.PropsWithChildren<AuditTrailWrapperProps>
> = ({
    targetModel,
    targetId,
    filterMode = 'url',
    hideHeading,
    heading = 'Audit Trail',
}) => {
    const { data, isLoading, isError, refetch } = useActivityLogsQuery({
        target_model: targetModel,
        target_id: targetId,
    });
    const [filters, setFilter] = useAuditTrailFilterState(filterMode);
    const { resolveUser } = useAuditUserResolver();

    const personFilterOptions = useMemo(() => {
        if (!data || data.length === 0) return [];

        const userMap = new Map<number, string>();
        data.forEach((item) => {
            if (!userMap.has(item.user_id)) {
                userMap.set(
                    item.user_id,
                    resolveUser(item.user_id, item.user_name),
                );
            }
        });

        return Array.from(userMap.entries()).map(([user_id, label]) => ({
            label,
            value: user_id,
        }));
    }, [data, resolveUser]);

    return (
        <>
            {!hideHeading && (
                <HeaderContainer>
                    <Heading>{heading}</Heading>
                    <Right>
                        <AuditTrailFilters
                            personFilterOptions={personFilterOptions}
                            value={filters}
                            onChange={setFilter}
                            // Local mode lives in drawers, whose scrolling body clips menus.
                            menuPortalTarget={
                                filterMode === 'local'
                                    ? document.body
                                    : undefined
                            }
                        />
                    </Right>
                </HeaderContainer>
            )}
            {isLoading ? (
                <div data-testid="audit-trail-loading">
                    <LoadingSpinner />
                </div>
            ) : isError ? (
                <Muted>
                    Couldn&apos;t load history.{' '}
                    <LinkButton type="button" onClick={() => refetch()}>
                        Retry
                    </LinkButton>
                </Muted>
            ) : (
                <AuditTrailViewWrapper
                    data={data ?? []}
                    isLoading={false}
                    filters={filters}
                />
            )}
        </>
    );
};

export default AuditTrailWrapper;

const HeaderContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
});

const Heading = styled.span({
    fontSize: aampeTheme.size.font.heading,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.tall,
    color: aampeTheme.colors.gray800,
});

const Right = styled.div({
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
});

const Muted = styled.div({
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG01,
});

const LinkButton = styled.button({
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.solidA01,
});
